import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Plus, Trash2, Loader2, Hammer, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { estimateApi } from '../lib/api';

const PROFIT_OPTIONS = [10, 20, 30, 40, 50];

function money(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function blankLine() {
  return { description: '', material: '', labor_hours: '', equipment: '', subcontract: '' };
}

/**
 * In-app manual estimate builder. Mirrors the contractor cost buildup:
 * COGS (material + material tax + labor + equipment + subs) -> +CYA -> +Overhead -> +Profit -> Sales Price.
 * tax %, CYA %, overhead % come from org defaults (owner/manager-controlled in Settings).
 */
export default function ManualEstimateBuilder({
  open, onOpenChange, leadId, projectId, defaults, editing = null, onSaved,
}) {
  const rates = editing
    ? {
        material_tax_pct: editing.totals?.tax_pct ?? defaults.material_tax_pct,
        cya_pct: editing.totals?.contingency_pct ?? defaults.cya_pct,
        overhead_pct: editing.totals?.overhead_pct ?? defaults.overhead_pct,
      }
    : defaults;

  const [name, setName] = useState(editing?.estimate_name || 'Manual Estimate');
  const [laborRate, setLaborRate] = useState(
    editing?.labor_rate != null ? String(editing.labor_rate) : String(defaults.labor_rate ?? 42)
  );
  const [profitPct, setProfitPct] = useState(
    editing?.totals?.profit_pct != null ? String(editing.totals.profit_pct) : '20'
  );
  const [lines, setLines] = useState(
    editing?.line_items?.length
      ? editing.line_items.map((li) => ({
          description: li.description || '',
          material: li.material || '',
          labor_hours: li.labor_hours || '',
          equipment: li.equipment || '',
          subcontract: li.subcontract || '',
        }))
      : [blankLine()]
  );
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadPdf = async (internal) => {
    setDownloadingPdf(true);
    try {
      const res = await estimateApi.getPdf(editing.id, internal);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Estimate-${editing.estimate_number || editing.id.slice(0, 8)}${internal ? '-internal' : ''}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const updateLine = (idx, field, value) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };
  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const totals = useMemo(() => {
    const rate = parseFloat(laborRate) || 0;
    let material = 0, labor = 0, equipment = 0, subcontract = 0, hours = 0;
    lines.forEach((l) => {
      material += parseFloat(l.material) || 0;
      const h = parseFloat(l.labor_hours) || 0;
      hours += h;
      labor += rate * h;
      equipment += parseFloat(l.equipment) || 0;
      subcontract += parseFloat(l.subcontract) || 0;
    });
    const tax = material * (rates.material_tax_pct || 0) / 100;
    const cogs = material + tax + labor + equipment + subcontract;
    const contingency = cogs * (rates.cya_pct || 0) / 100;
    const subTotal = cogs + contingency;
    const overhead = subTotal * (rates.overhead_pct || 0) / 100;
    const projectCost = subTotal + overhead;
    const profit = projectCost * (parseFloat(profitPct) || 0) / 100;
    const salesPrice = projectCost + profit;
    return { material, labor, equipment, subcontract, hours, tax, cogs, contingency, overhead, profit, salesPrice };
  }, [lines, laborRate, profitPct, rates]);

  const lineTotal = (l) => {
    const rate = parseFloat(laborRate) || 0;
    return (parseFloat(l.material) || 0) + rate * (parseFloat(l.labor_hours) || 0)
      + (parseFloat(l.equipment) || 0) + (parseFloat(l.subcontract) || 0);
  };

  const handleSave = async () => {
    const cleanLines = lines
      .filter((l) => l.description.trim() || l.material || l.labor_hours || l.equipment || l.subcontract)
      .map((l) => ({
        description: l.description.trim(),
        material: parseFloat(l.material) || 0,
        labor_hours: parseFloat(l.labor_hours) || 0,
        equipment: parseFloat(l.equipment) || 0,
        subcontract: parseFloat(l.subcontract) || 0,
      }));
    if (cleanLines.length === 0) {
      toast.error('Add at least one line with a description or cost');
      return;
    }
    const payload = {
      name: name.trim() || 'Manual Estimate',
      line_items: cleanLines,
      labor_rate: parseFloat(laborRate) || 0,
      profit_pct: parseFloat(profitPct) || 0,
    };
    setSaving(true);
    try {
      if (editing) {
        await estimateApi.updateManual(editing.id, payload);
        toast.success('Estimate updated');
      } else if (projectId) {
        await estimateApi.createManualForProject(projectId, payload);
        toast.success('Estimate created');
      } else {
        await estimateApi.createManualForLead(leadId, payload);
        toast.success('Estimate created');
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="manual-estimate-builder">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hammer className="h-5 w-5 text-orange-500" />
            {editing ? 'Edit Estimate' : 'Build Estimate'}
          </DialogTitle>
          <DialogDescription>
            Add each step's materials, labor hours, equipment and subs. Markup is applied automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Estimate Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Garage wall build + paint" className="rounded-sm mt-1"
              data-testid="manual-est-name" />
          </div>

          {/* Line items */}
          <div className="border rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                  <th className="text-left px-3 py-2 font-medium min-w-[180px]">Step / Item</th>
                  <th className="text-right px-2 py-2 font-medium w-24">Material $</th>
                  <th className="text-right px-2 py-2 font-medium w-20">Labor hrs</th>
                  <th className="text-right px-2 py-2 font-medium w-24">Equip. $</th>
                  <th className="text-right px-2 py-2 font-medium w-24">Subs $</th>
                  <th className="text-right px-2 py-2 font-medium w-24">Line Total</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={idx} className="border-b last:border-0" data-testid={`manual-line-${idx}`}>
                    <td className="px-2 py-1.5">
                      <Input value={l.description} onChange={(e) => updateLine(idx, 'description', e.target.value)}
                        placeholder="e.g. 2x4x8 lumber" className="h-8 text-sm rounded-sm"
                        data-testid={`manual-line-desc-${idx}`} />
                    </td>
                    <td className="px-1 py-1.5">
                      <Input type="number" step="0.01" min="0" value={l.material}
                        onChange={(e) => updateLine(idx, 'material', e.target.value)}
                        placeholder="0" className="h-8 text-sm text-right rounded-sm"
                        data-testid={`manual-line-material-${idx}`} />
                    </td>
                    <td className="px-1 py-1.5">
                      <Input type="number" step="0.1" min="0" value={l.labor_hours}
                        onChange={(e) => updateLine(idx, 'labor_hours', e.target.value)}
                        placeholder="0" className="h-8 text-sm text-right rounded-sm"
                        data-testid={`manual-line-hours-${idx}`} />
                    </td>
                    <td className="px-1 py-1.5">
                      <Input type="number" step="0.01" min="0" value={l.equipment}
                        onChange={(e) => updateLine(idx, 'equipment', e.target.value)}
                        placeholder="0" className="h-8 text-sm text-right rounded-sm"
                        data-testid={`manual-line-equip-${idx}`} />
                    </td>
                    <td className="px-1 py-1.5">
                      <Input type="number" step="0.01" min="0" value={l.subcontract}
                        onChange={(e) => updateLine(idx, 'subcontract', e.target.value)}
                        placeholder="0" className="h-8 text-sm text-right rounded-sm"
                        data-testid={`manual-line-subs-${idx}`} />
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-medium">{money(lineTotal(l))}</td>
                    <td className="px-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                        onClick={() => removeLine(idx)} data-testid={`manual-remove-line-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm" data-testid="manual-add-line">
            <Plus className="h-4 w-4 mr-1" /> Add Step
          </Button>

          {/* Rate controls + totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Labor Rate ($/hr for worker/crew)</Label>
                <Input type="number" step="0.01" min="0" value={laborRate}
                  onChange={(e) => setLaborRate(e.target.value)}
                  className="h-9 rounded-sm mt-1" data-testid="manual-labor-rate" />
              </div>
              <div>
                <Label className="text-xs">Profit Markup</Label>
                <Select value={profitPct} onValueChange={setProfitPct}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="manual-profit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROFIT_OPTIONS.map((p) => (
                      <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Tax {rates.material_tax_pct}% (materials) · CYA {rates.cya_pct}% · Overhead {rates.overhead_pct}% —
                set by owner/manager in Settings.
              </p>
            </div>

            <div className="bg-muted/40 rounded-sm p-4 text-sm space-y-1.5" data-testid="manual-totals">
              <Row label="Material" value={money(totals.material)} />
              <Row label={`Material Sales Tax (${rates.material_tax_pct}%)`} value={money(totals.tax)} />
              <Row label={`Labor (${totals.hours || 0} hrs)`} value={money(totals.labor)} />
              {totals.equipment > 0 && <Row label="Equipment" value={money(totals.equipment)} />}
              {totals.subcontract > 0 && <Row label="Subcontractors" value={money(totals.subcontract)} />}
              <Row label="COGS Subtotal" value={money(totals.cogs)} bold />
              <Row label={`Contingency / CYA (${rates.cya_pct}%)`} value={money(totals.contingency)} />
              <Row label={`Overhead (${rates.overhead_pct}%)`} value={money(totals.overhead)} />
              <Row label={`Profit (${profitPct}%)`} value={money(totals.profit)} />
              <div className="flex items-center justify-between border-t pt-2 mt-2">
                <span className="font-semibold">Sales Price</span>
                <span className="text-xl font-bold text-orange-600 tabular-nums" data-testid="manual-sales-price">
                  {money(totals.salesPrice)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 flex-wrap">
            {editing && (
              <>
                <Button variant="outline" onClick={() => downloadPdf(false)} disabled={downloadingPdf}
                  className="rounded-sm mr-auto" data-testid="manual-client-pdf-btn">
                  {downloadingPdf ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}
                  Client PDF
                </Button>
                <Button variant="outline" onClick={() => downloadPdf(true)} disabled={downloadingPdf}
                  className="rounded-sm" data-testid="manual-internal-pdf-btn">
                  <FileDown className="h-4 w-4 mr-1" /> Internal PDF
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving}
              className="rounded-sm bg-orange-500 hover:bg-orange-600" data-testid="manual-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save Changes' : 'Create Estimate'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-muted-foreground ${bold ? 'font-medium text-foreground' : ''}`}>{label}</span>
      <span className={`tabular-nums ${bold ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  );
}
